import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { IconButton, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { BLEND_TYPES, type BlendChild, type BlendTree, type BlendType, type Motion, type Param } from './controller-doc.js';

export const CLIP_LIST_ID = 'anim-clip-refs';

const is2d = (t: BlendType) => t === 'simpleDirectional2d' || t === 'freeformCartesian2d';

/** Clip reference or nested tree, with a toggle between the two. */
export function MotionField({
  motion,
  params,
  onSet,
  mutateTree,
  depth = 0,
}: {
  motion: Motion | undefined;
  params: readonly Param[];
  onSet: (m: Motion | undefined) => void;
  mutateTree: (fn: (t: BlendTree) => void) => void;
  depth?: number;
}) {
  const kind = motion === undefined ? 'none' : typeof motion === 'string' ? 'clip' : 'tree';
  const numeric = params.filter((p) => p.type === 'float' || p.type === 'int').map((p) => p.name);
  return (
    <div className="anim-motion">
      <SelectField
        value={kind}
        options={[
          { value: 'none', label: 'Empty' },
          { value: 'clip', label: 'Clip' },
          { value: 'tree', label: 'Blend tree' },
        ]}
        onChange={(k) => {
          if (k === kind) return;
          if (k === 'none') onSet(undefined);
          else if (k === 'clip') onSet('');
          else onSet({ type: '1d', parameter: numeric[0] ?? '', children: [] });
        }}
      />
      {typeof motion === 'string' && <TextField mono list={CLIP_LIST_ID} value={motion} placeholder="models/anims.glb#Run or embedded name" onChange={(v) => onSet(v)} />}
      {motion !== undefined && typeof motion !== 'string' && <BlendTreeEditor tree={motion} params={params} mutate={mutateTree} depth={depth} />}
    </div>
  );
}

/** A blend tree: type, parameters and children (1D thresholds or 2D positions with a point editor). */
export function BlendTreeEditor({
  tree,
  params,
  mutate,
  depth = 0,
}: {
  tree: BlendTree;
  params: readonly Param[];
  mutate: (fn: (t: BlendTree) => void) => void;
  depth?: number;
}) {
  const numeric = params.filter((p) => p.type === 'float' || p.type === 'int').map((p) => p.name);
  const [selected, setSelected] = useState<number | null>(null);
  const children = tree.children ?? [];

  const addChild = () =>
    mutate((t) => {
      const c: BlendChild = { motion: '' };
      if (t.type === '1d') c.threshold = t.children.length ? (t.children[t.children.length - 1]!.threshold ?? 0) + 1 : 0;
      else if (is2d(t.type)) c.position = [0, 0];
      else c.directParameter = numeric[0] ?? '';
      t.children = [...(t.children ?? []), c];
    });

  return (
    <div className={`anim-tree depth-${Math.min(depth, 3)}`}>
      <PropRow label="Type">
        <SelectField
          value={tree.type}
          options={BLEND_TYPES}
          onChange={(v) =>
            mutate((t) => {
              t.type = v as BlendType;
              for (const c of t.children) {
                if (t.type === '1d') c.threshold ??= 0;
                else if (is2d(t.type)) c.position ??= [0, 0];
                else c.directParameter ??= numeric[0] ?? '';
              }
            })
          }
        />
      </PropRow>
      {tree.type !== 'direct' && (
        <PropRow label={is2d(tree.type) ? 'Parameter X' : 'Parameter'}>
          <SelectField value={tree.parameter} options={numeric} placeholder="float / int parameter" onChange={(v) => mutate((t) => void (t.parameter = v))} />
        </PropRow>
      )}
      {is2d(tree.type) && (
        <PropRow label="Parameter Y">
          <SelectField value={tree.parameterY} options={numeric} placeholder="float / int parameter" onChange={(v) => mutate((t) => void (t.parameterY = v))} />
        </PropRow>
      )}
      {is2d(tree.type) && children.length > 0 && (
        <PointEditor
          points={children.map((c) => c.position ?? [0, 0])}
          selected={selected}
          onSelect={setSelected}
          onMove={(i, p) => mutate((t) => void (t.children[i]!.position = p))}
        />
      )}
      <div className="anim-section-head">
        <span>Motions</span>
        <button type="button" className="anim-chip" onClick={addChild}>
          + motion
        </button>
      </div>
      {children.map((c, i) => (
        <div key={i} className={`anim-child${selected === i ? ' is-selected' : ''}`} onFocus={() => setSelected(i)}>
          <div className="anim-child-row">
            <span className="anim-child-index">{i + 1}</span>
            {tree.type === '1d' && (
              <NumberField label="th" title="Threshold" value={c.threshold ?? 0} step={0.1} onChange={(v) => mutate((t) => void (t.children[i]!.threshold = v))} />
            )}
            {is2d(tree.type) && (
              <>
                <NumberField axis="x" value={c.position?.[0] ?? 0} step={0.1} onChange={(v) => mutate((t) => void (t.children[i]!.position = [v, t.children[i]!.position?.[1] ?? 0]))} />
                <NumberField axis="y" value={c.position?.[1] ?? 0} step={0.1} onChange={(v) => mutate((t) => void (t.children[i]!.position = [t.children[i]!.position?.[0] ?? 0, v]))} />
              </>
            )}
            {tree.type === 'direct' && (
              <SelectField value={c.directParameter} options={numeric} placeholder="weight parameter" onChange={(v) => mutate((t) => void (t.children[i]!.directParameter = v))} />
            )}
            <NumberField label="×" title="Time scale" value={c.timeScale ?? 1} step={0.05} onChange={(v) => mutate((t) => (v === 1 ? delete t.children[i]!.timeScale : (t.children[i]!.timeScale = v)))} />
            <Toggle checked={c.mirror === true} label="M" onChange={(v) => mutate((t) => (v ? (t.children[i]!.mirror = true) : delete t.children[i]!.mirror))} />
            <IconButton icon="trash" title="Remove motion" onClick={() => mutate((t) => void t.children.splice(i, 1))} />
          </div>
          <MotionField
            motion={c.motion}
            params={params}
            depth={depth + 1}
            onSet={(m) => mutate((t) => void (t.children[i]!.motion = m ?? ''))}
            mutateTree={(fn) => mutate((t) => fn(t.children[i]!.motion as BlendTree))}
          />
        </div>
      ))}
    </div>
  );
}

/** Drag the children of a 2D tree around parameter space. */
function PointEditor({
  points,
  selected,
  onSelect,
  onMove,
}: {
  points: [number, number][];
  selected: number | null;
  onSelect: (i: number) => void;
  onMove: (i: number, p: [number, number]) => void;
}) {
  const size = 200;
  const ref = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<{ i: number; p: [number, number] } | null>(null);
  const extent = Math.max(1, ...points.flatMap((p) => [Math.abs(p[0]), Math.abs(p[1])])) * 1.2;
  const toScreen = (p: [number, number]) => [((p[0] / extent + 1) * size) / 2, ((1 - p[1] / extent) * size) / 2] as const;
  const toParam = (x: number, y: number): [number, number] => {
    const r = ref.current!.getBoundingClientRect();
    const px = (((x - r.left) / r.width) * 2 - 1) * extent;
    const py = (1 - ((y - r.top) / r.height) * 2) * extent;
    return [Math.round(px * 20) / 20, Math.round(py * 20) / 20];
  };
  const shown = points.map((p, i) => (drag && drag.i === i ? drag.p : p));
  return (
    <svg
      ref={ref}
      className="anim-points"
      viewBox={`0 0 ${size} ${size}`}
      onPointerMove={(e: ReactPointerEvent<SVGSVGElement>) => drag && setDrag({ i: drag.i, p: toParam(e.clientX, e.clientY) })}
      onPointerUp={() => {
        if (drag) onMove(drag.i, drag.p);
        setDrag(null);
      }}
    >
      <line x1={size / 2} y1={0} x2={size / 2} y2={size} className="anim-points-axis" />
      <line x1={0} y1={size / 2} x2={size} y2={size / 2} className="anim-points-axis" />
      {shown.map((p, i) => {
        const [x, y] = toScreen(p);
        return (
          <g
            key={i}
            className={`anim-point${selected === i ? ' is-selected' : ''}`}
            onPointerDown={(e) => {
              (e.currentTarget.ownerSVGElement ?? e.currentTarget).setPointerCapture?.(e.pointerId);
              onSelect(i);
              setDrag({ i, p });
            }}
          >
            <circle cx={x} cy={y} r={7} />
            <text x={x} y={y + 3.5}>
              {i + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
